import React, { useState, useEffect, useCallback } from 'react';
import { leaveApplicationAPI, employeeAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  FileText,
  ChevronLeft,
  ChevronRight,
  Users,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEAVE_TYPE_COLORS = {
  'Casual Leave': 'bg-blue-100 text-blue-700 border-blue-300',
  'Sick Leave': 'bg-red-100 text-red-700 border-red-300',
  'Earned/Privilege Leave': 'bg-green-100 text-green-700 border-green-300',
  'Comp-off': 'bg-purple-100 text-purple-700 border-purple-300',
  'Unpaid Leave': 'bg-slate-100 text-slate-700 border-slate-300',
  'Maternity Leave': 'bg-pink-100 text-pink-700 border-pink-300',
  'Paternity Leave': 'bg-cyan-100 text-cyan-700 border-cyan-300',
};

const LeaveApprovals = () => {
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingApplications, setPendingApplications] = useState([]);
  const [calendarData, setCalendarData] = useState({ leaves: [] });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [reportData, setReportData] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [actionType, setActionType] = useState('approve');
  const [adminComment, setAdminComment] = useState('');
  const [processing, setProcessing] = useState(false);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth() + 1;

  const fetchPendingApplications = useCallback(async () => {
    try {
      const response = await leaveApplicationAPI.getPending();
      setPendingApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch pending applications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCalendarData = useCallback(async () => {
    try {
      const response = await leaveApplicationAPI.getCalendar(year, month);
      setCalendarData(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    }
  }, [year, month]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await employeeAPI.getAll();
      setEmployees(response.data.filter(e => e.is_active));
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    if (!selectedEmployee) {
      setReportData(null);
      return;
    }
    try {
      const response = await leaveApplicationAPI.getReport(selectedEmployee, reportYear);
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    }
  }, [selectedEmployee, reportYear]);

  useEffect(() => {
    fetchPendingApplications();
    fetchEmployees();
  }, [fetchPendingApplications, fetchEmployees]);

  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendarData();
    }
  }, [activeTab, fetchCalendarData]);

  useEffect(() => {
    if (activeTab === 'report') {
      fetchReport();
    }
  }, [activeTab, fetchReport]);

  const openActionModal = (application, action) => {
    setSelectedApplication(application);
    setActionType(action);
    setAdminComment('');
    setIsActionModalOpen(true);
  };

  const handleAction = async () => {
    if (actionType === 'reject' && !adminComment.trim()) {
      alert('Comment is required for rejection');
      return;
    }
    
    setProcessing(true);
    try {
      await leaveApplicationAPI.update(selectedApplication.id, {
        status: actionType === 'approve' ? 'approved' : 'rejected',
        admin_comment: adminComment || null,
      });
      await fetchPendingApplications();
      setIsActionModalOpen(false);
    } catch (error) {
      console.error('Failed to process application:', error);
      alert(error.response?.data?.detail || 'Failed to process application');
    } finally {
      setProcessing(false);
    }
  };

  const handleMonthChange = (offset) => {
    setCurrentMonth(new Date(year, month - 1 + offset, 1));
  };

  const getLeaveTypeColor = (typeName) => {
    return LEAVE_TYPE_COLORS[typeName] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const getDaysInMonth = () => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = () => new Date(year, month - 1, 1).getDay();

  const getDocUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Day headers
    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={`header-${i}`} className="text-center text-xs font-semibold text-slate-500 py-2">
          {dayNames[i]}
        </div>
      );
    }

    // Empty cells
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-1"></div>);
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // Find leaves for this day
      const leavesOnDay = calendarData.leaves.filter(leave => {
        return dateStr >= leave.start_date && dateStr <= leave.end_date;
      });

      days.push(
        <div
          key={day}
          className="p-1 min-h-[80px] border border-slate-100 rounded"
        >
          <span className="text-xs font-medium text-slate-600">{day}</span>
          <div className="mt-1 space-y-0.5">
            {leavesOnDay.slice(0, 3).map((leave, idx) => (
              <div
                key={idx}
                className={`text-[10px] px-1 py-0.5 rounded truncate ${getLeaveTypeColor(leave.leave_type_name)}`}
                title={`${leave.employee_name} - ${leave.leave_type_name}`}
              >
                {leave.employee_name.split(' ')[0]}
              </div>
            ))}
            {leavesOnDay.length > 3 && (
              <div className="text-[10px] text-slate-500">+{leavesOnDay.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="leave-approvals-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Leave Management</h2>
          <p className="text-slate-500 text-sm mt-1">
            Approve requests, view team calendar, and generate reports
          </p>
        </div>
        {pendingApplications.length > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm">
            <Clock className="w-4 h-4" />
            {pendingApplications.length} pending approval{pendingApplications.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="pending" data-testid="tab-pending">
            <Clock className="w-4 h-4 mr-2" />
            Pending ({pendingApplications.length})
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <Calendar className="w-4 h-4 mr-2" />
            Team Calendar
          </TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report">
            <FileText className="w-4 h-4 mr-2" />
            Leave Report
          </TabsTrigger>
        </TabsList>

        {/* Pending Approvals */}
        <TabsContent value="pending" className="space-y-5">
          {pendingApplications.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-6 text-center">
              <CheckCircle className="w-12 h-9 text-green-500 mx-auto mb-3" />
              <p className="text-slate-600">No pending leave requests</p>
            </div>
          ) : (
            <div className="space-y-5">
              {pendingApplications.map((app) => (
                <div
                  key={app.id}
                  className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
                  data-testid={`pending-app-${app.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-[#1D35A0]">{app.employee_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLeaveTypeColor(app.leave_type_name)}`}>
                          {app.leave_type_name}
                        </span>
                        {app.is_unpaid_warning && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            No Balance
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Dates</p>
                          <p className="font-medium">
                            {app.start_date === app.end_date 
                              ? app.start_date 
                              : `${app.start_date} to ${app.end_date}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Days</p>
                          <p className="font-medium">
                            {app.working_days} {app.is_half_day ? `(${app.half_day_type} half)` : ''}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Current Balance</p>
                          <p className={`font-medium ${app.current_balance <= 0 ? 'text-red-600' : ''}`}>
                            {app.current_balance} days
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">Applied</p>
                          <p className="font-medium">
                            {new Date(app.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-3">
                        <p className="text-slate-500 text-sm">Reason</p>
                        <p className="text-sm mt-1">{app.reason}</p>
                      </div>
                      
                      {app.document_url && (
                        <a
                          href={getDocUrl(app.document_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                        >
                          <FileText className="w-4 h-4" />
                          View Document
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openActionModal(app, 'reject')}
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid={`reject-btn-${app.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => openActionModal(app, 'approve')}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-btn-${app.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Team Calendar */}
        <TabsContent value="calendar" className="space-y-5">
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={() => handleMonthChange(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="text-lg font-semibold text-[#1D35A0]">
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </h3>
              <Button variant="outline" size="sm" onClick={() => handleMonthChange(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-2 mb-4 text-xs">
              {Object.entries(LEAVE_TYPE_COLORS).map(([name, color]) => (
                <div key={name} className="flex items-center gap-1">
                  <div className={`w-3 h-3 rounded ${color.split(' ')[0]}`}></div>
                  <span className="text-slate-600">{name}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1" data-testid="team-calendar">
              {renderCalendar()}
            </div>
          </div>
        </TabsContent>

        {/* Leave Report */}
        <TabsContent value="report" className="space-y-5">
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Label className="mb-2 block">Select Employee</Label>
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger data-testid="select-report-employee">
                    <SelectValue placeholder="Choose an employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-32">
                <Label className="mb-2 block">Year</Label>
                <Select value={String(reportYear)} onValueChange={(v) => setReportYear(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!selectedEmployee ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Select an employee to view their leave report</p>
              </div>
            ) : reportData ? (
              <div className="space-y-5">
                {/* Employee Info */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-[#1D35A0]">{reportData.employee.full_name}</h4>
                  <p className="text-sm text-slate-600">{reportData.employee.designation}</p>
                </div>

                {/* Balances Summary */}
                <div>
                  <h4 className="font-semibold text-[#1D35A0] mb-3">Leave Balances</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {reportData.balances.map((bal) => (
                      <div key={bal.id} className="bg-white border border-slate-200 rounded-lg p-3">
                        <p className="text-xs text-slate-500 truncate">{bal.leave_type_name}</p>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className={`text-xl font-bold ${bal.balance <= 0 ? 'text-red-600' : 'text-[#1D35A0]'}`}>
                            {bal.balance}
                          </span>
                          <span className="text-sm text-slate-400">/ {bal.entitlement}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Used: {bal.used}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Leave History */}
                <div>
                  <h4 className="font-semibold text-[#1D35A0] mb-3">Leave History ({reportYear})</h4>
                  {reportData.applications.length === 0 ? (
                    <p className="text-slate-500 text-sm">No leave applications this year</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Dates</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase">Days</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {reportData.applications.map((app) => (
                            <tr key={app.id}>
                              <td className="px-3 py-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getLeaveTypeColor(app.leave_type_name)}`}>
                                  {app.leave_type_name}
                                </span>
                              </td>
                              <td className="px-3 py-2">{app.start_date} to {app.end_date}</td>
                              <td className="px-3 py-2 text-center">{app.working_days}</td>
                              <td className="px-3 py-2 text-center">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  app.status === 'approved' ? 'bg-green-100 text-green-700' :
                                  app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1D35A0]"></div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Approve/Reject Modal */}
      <Dialog open={isActionModalOpen} onOpenChange={setIsActionModalOpen}>
        <DialogContent className="max-w-md" data-testid="action-modal">
          <DialogHeader>
            <DialogTitle className={`text-xl font-bold ${actionType === 'approve' ? 'text-green-700' : 'text-red-700'}`}>
              {actionType === 'approve' ? 'Approve Leave' : 'Reject Leave'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {selectedApplication?.employee_name} - {selectedApplication?.leave_type_name}
              <br />
              {selectedApplication?.start_date} to {selectedApplication?.end_date} ({selectedApplication?.working_days} days)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>
                Comment {actionType === 'reject' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
                placeholder={actionType === 'reject' ? 'Reason for rejection (required)' : 'Optional comment'}
                rows={3}
                data-testid="input-comment"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsActionModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAction}
                className={actionType === 'approve' 
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
                }
                disabled={processing || (actionType === 'reject' && !adminComment.trim())}
                data-testid="confirm-action-btn"
              >
                {processing ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveApprovals;
